import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

const Home = () => {
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load websites from the JSON file
  useEffect(() => {
    const loadWebsites = async () => {
      try {
        // Fetch the websites.json file from the public directory
        const response = await fetch('/websites.json');
        if (!response.ok) {
          throw new Error('Failed to load websites configuration');
        }
        
        const data = await response.json();
        
        // Transform the websites data to match our component's expected format
        const transformedWebsites = data.websites
          .filter(website => website.active) // Only show active websites
          .map((website, index) => ({
            id: index + 1,
            url: website.displayUrl,
            displayUrl: website.displayUrl,
            icon: website.icon,
            fullUrl: website.url,
            screenshot: `/screenshots/${website.filename}` // Generated by screenshot service
          }));
        
        setWebsites(transformedWebsites);
        setLoading(false);
      } catch (err) {
        console.error('Error loading websites:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    loadWebsites();
  }, []);

  // Screenshots are now handled by the screenshot service
  // No external APIs needed - images are generated locally and served from /public/screenshots/

  // Component for website card with local screenshot
  const WebsiteCard = ({ website }) => {
    const [imageError, setImageError] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    return (
      <a
        href={website.fullUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="website-card-link"
        aria-label={`Open ${website.displayUrl} in a new tab`}
      >
        <div className="website-card">
          <div className="website-screenshot">
            {!imageError ? (
              <>
                <img 
                  src={website.screenshot}
                  alt={`Screenshot of ${website.displayUrl}`}
                  className={`screenshot-image ${imageLoaded ? 'loaded' : ''}`}
                  onLoad={() => setImageLoaded(true)}
                  onError={() => setImageError(true)}
                  style={{ display: imageLoaded ? 'block' : 'none' }}
                />
                {!imageLoaded && (
                  <div className="screenshot-placeholder loading">
                    <i className={website.icon}></i>
                    <span className="loading-text">Loading...</span>
                  </div>
                )}
              </>
            ) : (
              <div className="screenshot-placeholder">
                <i className={website.icon}></i>
              </div>
            )}
          </div>
          <div className="website-info">
            <div className="website-icon">
              <i className={website.icon}></i>
            </div>
            <div className="website-url">{website.displayUrl}</div>
          </div>
        </div>
      </a>
    );
  };
  return (
    <div className="home-container">
      <Link to="/services" className="services-button-home">
        Services
      </Link>
      {/* Left Column - Company Overview */}
      <div className="left-column">
        <div className="company-info">
          {/* Logo */}
          <div className="logo">
            <img src="/logo.png" alt="FLYGON LC" className="logo-image" />
          </div>

          {/* Services List */}
          <div className="services-list">
            <div className="service-item">Software + Website Development</div>
            <div className="service-item">Digital Advertising</div>
            <div className="service-item">Media Development + Management</div>
            <div className="service-item">Data Analysis</div>
          </div>

          {/* Business Details */}
          <div className="business-details">
            <div className="detail-item">Over $25M in Ad-Driven Sales</div>
            <div className="detail-item">In-House Support</div>
            <div className="detail-item">On-Site Training</div>
            <div className="detail-item">Based in Phoenix, AZ</div>
            <div className="detail-item">Est. 2020</div>
          </div>

          {/* Referral Only */}
          <div className="referral-only">REFERRAL ONLY</div>
        </div>
      </div>

      {/* Right Column - Website Showcase */}
      <div className="right-column">
        <div className="websites-showcase">
          <div className="websites-header">Select Websites</div>
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <div className="loading-text">Loading websites...</div>
            </div>
          ) : error ? (
            <div className="error-state">
              <div className="error-icon">⚠️</div>
              <div className="error-text">Failed to load websites: {error}</div>
            </div>
          ) : (
            <>
              {websites.map((website) => (
                <WebsiteCard key={website.id} website={website} />
              ))}
            </>
          )}

          {/* Copyright Section */}
          <div className="copyright-section">
            <div className="copyright-text">COPYRIGHT © 2020-2025 FLYGON LC</div>
            <div className="legal-links">
              <Link to="/privacy" className="legal-link">Privacy Policy</Link>
              <span className="separator"> - </span>
              <Link to="/terms" className="legal-link">Terms and Conditions</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
